import * as React from 'react';
import { useFlowium } from '../client/FlowiumContext';
import Sidebar from './Sidebar';
import Detailsbar from './Detailsbar';
import Navbar from './Navbar';
import type { FileSystemItem } from '@flowium/client';
import { getDisplayLabel } from '../utils';
import BetaLaunchBanner from '../components/BetaLaunchBanner';

export interface DetailsPanelProps {
    isDetailsbarCollapsed: boolean;
    setIsDetailsbarCollapsed: (value: boolean) => void;
    detailsbarContent: React.ReactNode;
    setDetailsbarContent: (content: React.ReactNode) => void;
    selectedItem: FileSystemItem | null;
    setSelectedItem: (item: FileSystemItem | null) => void;
}

interface AppContainerProps {
    title: string;
    children: React.ReactNode | ((props: DetailsPanelProps) => React.ReactNode);
    padding?: string;
    currentPath?: string;
    showBetaLaunchBanner?: boolean;
}

const AppContainer: React.FC<AppContainerProps> = ({
    title, children, padding = 'p-4', currentPath, showBetaLaunchBanner = false
}) => {
    const { currentWorkspace, currentProject } = useFlowium();
    const [isCollapsed, setIsCollapsed] = React.useState(true);
    const [content, setContent] = React.useState<React.ReactNode>(null);
    const [selectedItem, setSelectedItem] = React.useState<FileSystemItem | null>(null);

    const handleCollapse = React.useCallback(() => {
        setIsCollapsed(true);
        setSelectedItem(null);
        setContent(null);
    }, []);

    const detailsPanelProps: DetailsPanelProps = {
        isDetailsbarCollapsed: isCollapsed,
        setIsDetailsbarCollapsed: setIsCollapsed,
        detailsbarContent: content,
        setDetailsbarContent: setContent,
        selectedItem,
        setSelectedItem,
    };

    const renderedChildren = typeof children === 'function'
        ? children(detailsPanelProps)
        : React.Children.map(children, child => {
            if (React.isValidElement<DetailsPanelProps>(child)) {
                return React.cloneElement(child, detailsPanelProps);
            }
            return child;
        });

    return (
        <div className="flex h-screen min-w-0 overflow-hidden bg-surface text-content">
            <Sidebar />
            <div className="flex min-w-0 flex-1 flex-col">
                <Navbar
                    project={currentProject ? getDisplayLabel(currentProject.name, 'Untitled project') : ''}
                    workspace={getDisplayLabel(currentWorkspace?.name, 'No Workspace Selected')}
                    currentPath={currentPath}
                />
                <main className={`${padding} min-h-0 flex-1 overflow-hidden bg-surface`}>
                    {showBetaLaunchBanner && (
                        <div className="pb-4 pr-4 pl-4 md:pl-6 md:pr-6">
                            <BetaLaunchBanner />
                        </div>
                    )}
                    {renderedChildren}
                </main>
            </div>
            <Detailsbar
                isCollapsed={isCollapsed}
                onCollapse={handleCollapse}
                selectedItem={selectedItem}
            >
                {content}
            </Detailsbar>
        </div>
    );
};

export default AppContainer;
